import React, { useState, useEffect } from 'react';
import { CheckCircle2, Circle, Clock, AlertCircle, Sparkles, Award } from 'lucide-react';

const HorizontalTimeline = ({ phases }) => {
  const [animatedPhases, setAnimatedPhases] = useState(phases);
  const [progressAnimations, setProgressAnimations] = useState({});

  useEffect(() => {
    setAnimatedPhases(phases);
    
    // Initialize progress animations
    const animations = {};
    phases?.forEach(phase => {
      if (phase?.status === 'em_andamento') {
        animations[phase.id] = 0;
      }
    });
    setProgressAnimations(animations);

    // Animate progress bars
    const animateProgress = () => {
      setProgressAnimations(prev => {
        const updated = { ...prev };
        phases?.forEach(phase => {
          if (phase?.status === 'em_andamento') {
            updated[phase.id] = phase?.progressPercentage;
          }
        });
        return updated;
      });
    };

    const timeout = setTimeout(animateProgress, 300);
    
    // Auto-refresh progress every minute
    const interval = setInterval(() => {
      setAnimatedPhases([...phases]);
    }, 60000);

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [phases]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'concluido':
        return (
          <div className="relative">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            <Sparkles className="h-4 w-4 text-emerald-400 absolute -top-1 -right-1 animate-pulse" />
          </div>
        );
      case 'em_andamento':
        return <Clock className="h-8 w-8 text-indigo-600 animate-pulse" />;
      case 'atrasado':
        return <AlertCircle className="h-8 w-8 text-rose-600 animate-bounce" />;
      default:
        return <Circle className="h-8 w-8 text-slate-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'concluido':
        return 'bg-gradient-to-br from-emerald-500 to-teal-600';
      case 'em_andamento':
        return 'bg-gradient-to-br from-indigo-500 to-purple-600';
      case 'atrasado':
        return 'bg-gradient-to-br from-rose-500 to-pink-600';
      default:
        return 'bg-gradient-to-br from-slate-300 to-slate-400';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'concluido':
        return 'Concluído';
      case 'em_andamento':
        return 'Em Andamento';
      case 'atrasado':
        return 'Atrasado';
      default:
        return 'Aguardando';
    }
  };

  const getBackgroundStyle = (status) => {
    switch (status) {
      case 'concluido':
        return 'bg-gradient-to-br from-emerald-50 to-teal-50';
      case 'em_andamento':
        return 'bg-gradient-to-br from-indigo-50 to-purple-50';
      case 'atrasado':
        return 'bg-gradient-to-br from-rose-50 to-pink-50';
      default:
        return 'bg-gradient-to-br from-slate-50 to-slate-100';
    }
  };

  const getBadgeStyle = (status) => {
    switch (status) {
      case 'concluido':
        return 'bg-gradient-to-r from-emerald-100 to-teal-100 text-emerald-700 border-2 border-emerald-200 shadow-md';
      case 'em_andamento':
        return 'bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 border-2 border-indigo-200 shadow-md animate-pulse';
      case 'atrasado':
        return 'bg-gradient-to-r from-rose-100 to-pink-100 text-rose-700 border-2 border-rose-200 shadow-md';
      default:
        return 'bg-gradient-to-r from-slate-100 to-slate-200 text-slate-600 border-2 border-slate-200 shadow-sm';
    }
  };

  const getProgressBarStyle = (status) => {
    switch (status) {
      case 'concluido':
        return 'bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-500 bg-[length:200%_100%] animate-gradient';
      case 'em_andamento':
        return 'bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 bg-[length:200%_100%] animate-gradient';
      case 'atrasado':
        return 'bg-gradient-to-r from-rose-500 via-pink-500 to-rose-500 bg-[length:200%_100%] animate-gradient';
      default:
        return 'bg-slate-400';
    }
  };

  const getConnectorStyle = (status) => {
    switch (status) {
      case 'concluido':
        return 'bg-gradient-to-r from-emerald-400 to-teal-400';
      case 'em_andamento':
        return 'bg-gradient-to-r from-indigo-400 to-purple-400';
      default:
        return 'bg-slate-200';
    }
  };

  const getCardHoverEffect = (status) => {
    switch (status) {
      case 'concluido':
        return 'hover:shadow-emerald-200 hover:border-emerald-300';
      case 'em_andamento':
        return 'hover:shadow-indigo-200 hover:border-indigo-300 hover:scale-105';
      case 'atrasado':
        return 'hover:shadow-rose-200 hover:border-rose-300';
      default:
        return 'hover:shadow-slate-200 hover:border-slate-300';
    }
  };

  return (
    <div className="w-full">
      {/* Horizontal Scroll Container - Premium Design */}
      <div className="overflow-x-auto pb-6 sm:pb-8 scrollbar-thin scrollbar-thumb-indigo-300 scrollbar-track-slate-100 hover:scrollbar-thumb-indigo-400">
        <div className="flex items-start gap-4 sm:gap-6 lg:gap-8 min-w-max px-2 sm:px-4">
          {animatedPhases?.map((phase, index) => (
            <React.Fragment key={phase?.id}>
              {/* Phase Card - Optimized for All Devices */}
              <div className={`flex flex-col items-center w-[260px] sm:w-[280px] lg:w-[320px] transition-all duration-500 ${getCardHoverEffect(phase?.status)}`}>
                {/* Phase Icon with Layered Effects */}
                <div className="relative mb-5 sm:mb-6 group">
                  {/* Outer glow */}
                  <div className={`absolute inset-0 rounded-3xl ${getBackgroundStyle(phase?.status)} blur-2xl opacity-40 group-hover:opacity-60 transition-opacity duration-300`}></div>
                  
                  {/* Icon container */}
                  <div className={`relative p-5 sm:p-6 rounded-3xl shadow-2xl ${getBackgroundStyle(phase?.status)} border-2 border-white/60 backdrop-blur-sm transform group-hover:-translate-y-1 transition-all duration-300`}>
                    {getStatusIcon(phase?.status)}
                  </div>
                  
                  {/* Progress Ring for Active Phases */}
                  {phase?.status === 'em_andamento' && (
                    <div className="absolute inset-0 pointer-events-none">
                      <svg className="w-full h-full" style={{ transform: 'rotate(-90deg)' }}>
                        <circle
                          cx="50%"
                          cy="50%"
                          r="42"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          className="text-indigo-200 opacity-30"
                        />
                        <circle
                          cx="50%"
                          cy="50%"
                          r="42"
                          fill="none"
                          stroke="url(#progress-gradient)"
                          strokeWidth="3"
                          strokeDasharray={`${2 * Math.PI * 42}`}
                          strokeDashoffset={`${2 * Math.PI * 42 * (1 - (progressAnimations?.[phase?.id] || 0) / 100)}`}
                          className="transition-all duration-1000 ease-out drop-shadow-lg"
                          style={{ strokeLinecap: 'round' }}
                        />
                        <defs>
                          <linearGradient id="progress-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#6366f1" />
                            <stop offset="50%" stopColor="#8b5cf6" />
                            <stop offset="100%" stopColor="#a855f7" />
                          </linearGradient>
                        </defs>
                      </svg>
                    </div>
                  )}

                  {/* Completion Badge */}
                  {phase?.status === 'concluido' && (
                    <div className="absolute -top-2 -right-2 p-2 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full shadow-xl animate-bounce">
                      <Award className="h-4 w-4 text-white" />
                    </div>
                  )}
                </div>

                {/* Phase Info Card - Premium Style */}
                <div className="w-full space-y-4 bg-white/90 backdrop-blur-md rounded-3xl p-5 sm:p-6 shadow-2xl border-2 border-slate-100 transform transition-all duration-300 hover:shadow-3xl hover:-translate-y-1">
                  {/* Phase Name - Prominent */}
                  <h4 className="font-bold text-slate-800 text-lg sm:text-xl leading-tight text-center px-2 break-words min-h-[3rem] flex items-center justify-center">
                    {phase?.name}
                  </h4>
                  
                  {/* Status Badge - Eye-catching */}
                  <div className="flex justify-center">
                    <span className={`text-sm font-bold px-6 py-3 rounded-2xl inline-flex items-center gap-2 ${getBadgeStyle(phase?.status)}`}>
                      {phase?.status === 'em_andamento' && (
                        <div className="w-2 h-2 bg-current rounded-full animate-pulse"></div>
                      )}
                      {getStatusText(phase?.status)}
                    </span>
                  </div>

                  {/* Progress Section - Enhanced Visuals */}
                  {phase?.status === 'em_andamento' && (
                    <div className="space-y-3 p-4 bg-gradient-to-br from-indigo-50 via-purple-50 to-indigo-50 rounded-2xl border-2 border-indigo-100 shadow-inner">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-bold text-indigo-700">
                          Progresso
                        </p>
                        <p className="text-2xl font-black bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                          {progressAnimations?.[phase?.id] || 0}%
                        </p>
                      </div>
                      <div className="relative w-full bg-slate-200 rounded-full h-4 shadow-inner overflow-hidden">
                        <div
                          className={`h-4 rounded-full transition-all duration-1000 ease-out shadow-lg ${getProgressBarStyle(phase?.status)}`}
                          style={{ width: `${progressAnimations?.[phase?.id] || 0}%` }}
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer"></div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Date Information - Clean Layout */}
                  <div className="space-y-3 p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl border-2 border-slate-200">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold text-slate-600">Início</span>
                      <span className="font-bold text-slate-800">
                        {new Date(phase?.startDate)?.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </span>
                    </div>
                    <div className="h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent"></div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold text-slate-600">
                        {phase?.actualEndDate ? 'Concluído' : 'Prazo'}
                      </span>
                      <span className="font-bold text-slate-800">
                        {(phase?.actualEndDate 
                          ? new Date(phase?.actualEndDate)
                          : new Date(phase?.estimatedEndDate)
                        )?.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </span>
                    </div>
                  </div>

                  {/* Description - Elegant Typography */}
                  {phase?.description && (
                    <p className="text-sm text-slate-600 leading-relaxed bg-slate-50 rounded-2xl p-4 border-2 border-slate-200 break-words line-clamp-3">
                      {phase?.description}
                    </p>
                  )}
                </div>
              </div>

              {/* Connector Line - Smooth Transitions */}
              {index < animatedPhases?.length - 1 && (
                <div className="flex items-center self-start" style={{ marginTop: '72px' }}>
                  <div className="relative">
                    <div className={`h-2 w-16 sm:w-20 lg:w-24 rounded-full shadow-lg ${getConnectorStyle(phase?.status)} transition-all duration-500`}>
                      {phase?.status === 'em_andamento' && (
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent rounded-full animate-shimmer"></div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
      {/* Mobile Scroll Hint */}
      <div className="md:hidden text-center mt-4">
        <p className="text-xs text-slate-500 font-medium">
          ← Deslize para ver todas as etapas →
        </p>
      </div>
    </div>
  );
};

export default HorizontalTimeline;